import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Toaster } from "react-hot-toast";
import { Plus, ArrowLeft, Moon, Sun } from "lucide-react";
import { motion } from "framer-motion";

import { DiagramEditor } from "./components/DiagramEditor";
import { DiagramManager } from "./components/DiagramManager";
import { Sidebar } from "./components/Sidebar";
import { AuthWrapper } from "./components/auth/AuthWrapper";
import { useDiagramStore } from "./store/diagramStore";
import { ServiceType } from "./types/diagram.types";
import { v4 as uuidv4 } from "uuid";
import { ThemeProvider, useTheme } from "./context/ThemeContext";

const AppContainer = styled.div<{ $isDarkMode: boolean }>`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: ${(props) => (props.$isDarkMode ? "#1a1d23" : "#f5f5f5")};
  transition: background 0.3s ease;
`;

const TopBar = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: ${(props) => (props.$isDarkMode ? "#2c2f36" : "white")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, ${(props) => (props.$isDarkMode ? 0.3 : 0.08)});
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 100;
  transition: all 0.3s ease;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AppTitle = styled.h1<{ $isDarkMode: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#e9ecef" : "#333")};
  margin: 0;
  transition: color 0.3s ease;
`;

const DiagramName = styled.span<{ $isDarkMode: boolean }>`
  font-size: 16px;
  color: ${(props) => (props.$isDarkMode ? "#adb5bd" : "#666")};
  font-weight: 400;
  transition: color 0.3s ease;
`;

const IconButton = styled(motion.button)<{ $isDarkMode: boolean }>`
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.$isDarkMode ? "#adb5bd" : "#666")};
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)")};
    color: ${(props) => (props.$isDarkMode ? "#e9ecef" : "#333")};
  }
`;

const Button = styled(motion.button)`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover {
    opacity: 0.9;
  }
`;

const MainContent = styled.div`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
`;

function AppContent() {
  const [view, setView] = useState<"manager" | "editor">("manager");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentDiagram, loadDiagram, createNewDiagram, addNode } = useDiagramStore();
  const { isDarkMode, toggleTheme } = useTheme();

  const handleOpenDiagram = async (id: string) => {
    await loadDiagram(id);
    setView("editor");
  };

  const handleBackToManager = () => {
    setView("manager");
  };

  const handleAddService = () => {
    setSidebarOpen(true);
  };

  // Listen for sidebar open events from floating toolbar
  useEffect(() => {
    const handleOpenSidebar = () => {
      setSidebarOpen(true);
    };

    document.addEventListener("openSidebar", handleOpenSidebar);
    return () => document.removeEventListener("openSidebar", handleOpenSidebar);
  }, []);

  // Create a sample diagram on first load if none exist
  useEffect(() => {
    const initializeSampleDiagram = () => {
      const diagrams = useDiagramStore.getState().diagrams;
      if (diagrams.length === 0) {
        // Create sample FreshApp diagram
        createNewDiagram("FreshApp Service Architecture", "Communication flow between FreshApp and other services").catch(console.error);

        // Add sample nodes with IDs for convoy routing
        const freshAppId = uuidv4();
        const trackingServiceId = uuidv4();
        const freightServiceId = uuidv4();
        const legacyServiceId = uuidv4();
        const convoyId = uuidv4();
        const paymentServiceId = uuidv4();
        const nifiServiceId = uuidv4();
        const dashboardServiceId = uuidv4();
        const mfbErpId = uuidv4();

        const nodes = [
          {
            id: freshAppId,
            name: "FreshApp",
            type: "main" as ServiceType,
            position: { x: 100, y: 350 },
            description: "Main application service",
            endpoints: [],
          },
          {
            id: trackingServiceId,
            name: "Tracking Service",
            type: "internal" as ServiceType,
            position: { x: 700, y: 100 },
            description: "Handles shipment tracking",
            endpoints: [
              {
                id: uuidv4(),
                path: "/chargement/addTrackerToChargement",
                method: "POST" as const,
              },
              {
                id: uuidv4(),
                path: "webhook.freshtrack.ma/ingest/RDSEqqUWEzjmu2im",
                method: "POST" as const,
              },
            ],
          },
          {
            id: freightServiceId,
            name: "Freight Service",
            type: "external" as ServiceType,
            position: { x: 700, y: 200 },
            description: "External freight management",
            endpoints: [
              {
                id: uuidv4(),
                path: "/api/EventsManager/vizionCreateReference",
                method: "POST" as const,
              },
            ],
          },
          {
            id: legacyServiceId,
            name: "4D Legacy Service",
            type: "internal" as ServiceType,
            position: { x: 700, y: 500 },
            description: "Legacy 4D system integration",
            endpoints: [
              {
                id: uuidv4(),
                path: "/connectors/Adapter/newOrder-FreshApp",
                method: "POST" as const,
              },
              {
                id: uuidv4(),
                path: "/connectors/Listings/dupplicateShipment",
                method: "POST" as const,
              },
            ],
          },
          {
            id: convoyId,
            name: "Convoy System",
            type: "convoy" as ServiceType,
            position: { x: 400, y: 350 },
            description: "Webhook management and retry system",
            convoyRoutes: [
              {
                id: uuidv4(),
                name: "CONVOY_ERP_ORDER_ENDPOINT_ID",
                description: "MFB ERP Order endpoint",
                targetServiceId: mfbErpId,
                targetServiceName: "MFB ERP",
              },
              {
                id: uuidv4(),
                name: "CONVOY_ERP_PRICE_ENDPOINT_ID",
                description: "MFB ERP Price endpoint",
                targetServiceId: mfbErpId,
                targetServiceName: "MFB ERP",
              },
              {
                id: uuidv4(),
                name: "CONVOY_DASHBOARD_URL",
                description: "Dashboard sync endpoint",
                targetServiceId: dashboardServiceId,
                targetServiceName: "Dashboard Service",
              },
              {
                id: uuidv4(),
                name: "CONVOY_FOURD_SET_REFERENCE",
                description: "Set reference in 4D",
                targetServiceId: legacyServiceId,
                targetServiceName: "4D Legacy Service",
              },
              {
                id: uuidv4(),
                name: "CONVOY_TRACKING_ADD",
                description: "Add tracking info",
                targetServiceId: trackingServiceId,
                targetServiceName: "Tracking Service",
              },
            ],
          },
          {
            id: paymentServiceId,
            name: "Payment Service",
            type: "external" as ServiceType,
            position: { x: 700, y: 300 },
            description: "Payment processing service",
            endpoints: [
              {
                id: uuidv4(),
                path: "pay-egxsrs9r.freshtrack.ma/api/v1",
                method: "POST" as const,
              },
            ],
          },
          {
            id: nifiServiceId,
            name: "Nifi Document Processor",
            type: "internal" as ServiceType,
            position: { x: 700, y: 400 },
            description: "Document processing pipeline",
            endpoints: [
              {
                id: uuidv4(),
                path: "nifi-document-8g9zaolncy9.freshtrack.tech",
                method: "POST" as const,
              },
            ],
          },
          {
            id: dashboardServiceId,
            name: "Dashboard Service",
            type: "internal" as ServiceType,
            position: { x: 700, y: 600 },
            description: "Analytics and insights dashboard",
            endpoints: [
              {
                id: uuidv4(),
                path: "/api/insights/sync",
                method: "POST" as const,
              },
            ],
          },
          {
            id: mfbErpId,
            name: "MFB ERP",
            type: "external" as ServiceType,
            position: { x: 700, y: 700 },
            description: "External ERP system",
            endpoints: [
              {
                id: uuidv4(),
                path: "/api/orders",
                method: "POST" as const,
              },
              {
                id: uuidv4(),
                path: "/api/pricing",
                method: "POST" as const,
              },
            ],
          },
        ];

        nodes.forEach((node) => addNode(node));
      }
    };

    initializeSampleDiagram();
  }, [createNewDiagram, addNode]);

  return (
    <AppContainer $isDarkMode={isDarkMode}>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: isDarkMode ? "#2c2f36" : "#333",
            color: "#fff",
            borderRadius: "8px",
            padding: "12px 16px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#4CAF50",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#f44336",
              secondary: "#fff",
            },
          },
        }}
      />

      {view === "editor" && (
        <TopBar $isDarkMode={isDarkMode}>
          <TopBarLeft>
            <IconButton $isDarkMode={isDarkMode} onClick={handleBackToManager} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <ArrowLeft size={20} />
            </IconButton>
            <AppTitle $isDarkMode={isDarkMode}>
              Service Diagram Editor
              {currentDiagram && (
                <>
                  <span
                    style={{
                      margin: "0 8px",
                      color: isDarkMode ? "#6c757d" : "#999",
                    }}
                  >
                    •
                  </span>
                  <DiagramName $isDarkMode={isDarkMode}>{currentDiagram.name}</DiagramName>
                </>
              )}
            </AppTitle>
          </TopBarLeft>
          <TopBarRight>
            <IconButton $isDarkMode={isDarkMode} onClick={toggleTheme} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </IconButton>
            <Button onClick={handleAddService} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Plus size={18} />
              Add Service
            </Button>
          </TopBarRight>
        </TopBar>
      )}

      <MainContent style={{ top: view === "editor" ? 60 : 0 }}>
        {view === "manager" ? (
          <DiagramManager onOpenDiagram={handleOpenDiagram} />
        ) : (
          <>
            <DiagramEditor />
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          </>
        )}
      </MainContent>
    </AppContainer>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthWrapper>
        <AppContent />
      </AuthWrapper>
    </ThemeProvider>
  );
}

export default App;
